import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { updrsScoreSum, calculateLevodopaDBS, TDocument, sortAndTakeFirst, ftmtrsScore } from '../utils';

import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { isPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { sortDocuments } from 'Utility/randomUtil';
import { path } from 'Utility/ramdaReplacement';
import { difference } from 'ramda';
import colors from '../../../config/theme/colors';

const fm = (id: string): React.JSX.Element => <FormattedMessage id={id} />;

const updrsIIIValue = (d: IUPDRSIII) => {
  if (d.testType === 'UPDRS' && path(['retrospectiveData', 0], d) === true) {
    const manualScore = d.manualScore;
    return manualScore || manualScore === 0 ? `${manualScore}` : '';
  }
  const score = updrsScoreSum(d);
  return score || score === 0 ? `${score}` : '';
};

const ftmtrsValue = (d: IFTMTRS, fm: (s: string) => string) => {
  const whichHand = d.whichHand;

  const aScore = ftmtrsScore<'a'>(d, 'a');
  const bScoreRight =
    (whichHand === 'both' || whichHand === 'right') && ftmtrsScore<'b'>(d, 'b') && 'right' in ftmtrsScore<'b'>(d, 'b')!
      ? (ftmtrsScore<'b'>(d, 'b')!.right ?? null)
      : null;
  const bScoreLeft =
    (whichHand === 'both' || whichHand === 'left') && ftmtrsScore<'b'>(d, 'b') && 'left' in ftmtrsScore<'b'>(d, 'b')!
      ? (ftmtrsScore<'b'>(d, 'b')!.left ?? null)
      : null;

  const cScore = ftmtrsScore<'c'>(d, 'c');

  const rightString = bScoreRight || bScoreRight === 0 ? fm('updrs.rightShort') + bScoreRight : null;
  const leftString = bScoreLeft || bScoreLeft === 0 ? fm('updrs.leftShort') + bScoreLeft : null;

  return `${aScore ?? '-'} / ${rightString ?? ''} ${leftString ?? ''} / ${cScore ?? '-'}`;
};

const UpdrsDash = ({ documents }: IOwnProps): React.JSX.Element => {
  const latestDocuments = [
    sortAndTakeFirst('updrs_v', documents),
    sortAndTakeFirst('updrs_iii', documents),
    sortAndTakeFirst('ftmtrs', documents),
    sortAndTakeFirst('levodopatest', documents),
    sortAndTakeFirst('dbsEffect', documents),
  ];

  const sortedDocs = (latestDocuments.filter(d => d) as TDocument[]).sort((n1, n2) =>
    sortDocuments([{ type: 'date', sortField: 'date' }])(n1, n2),
  );

  const updrsDocTypes = ['updrs_v', 'updrs_iii', 'ftmtrs', 'levodopatest', 'dbsEffect'];

  const updrsMissingTypes = difference(
    updrsDocTypes,
    latestDocuments.map(d => d?._type),
  ).filter(t => t);

  const { formatMessage } = useIntl();

  const updrsTypesDisplayed = (type: string) =>
    ({
      updrs_v: formatMessage({ id: 'updrs.updrsV' }),
      updrs_iii: formatMessage({ id: 'updrs.updrsIII' }),
      ftmtrs: formatMessage({ id: 'updrs.ftmtrsShort' }),
      levodopatest: formatMessage({ id: 'updrs.levodopatest' }),
      dbsEffect: formatMessage({ id: 'updrs.dbsEffect' }),
    })[type];

  return (
    <>
      <TileContentMaker
        type="specificList"
        data={sortedDocs.map(d => {
          switch (d._type) {
            case 'updrs_v':
              return {
                date: isPartialDate(d?.date) ? formatPartialDate(d.date) : '',
                title: fm('updrs.updrsV'),
                value: (d as IUPDRSV).value,
                key: 'updrs_v',
              };

            case 'updrs_iii':
              return {
                date: isPartialDate(d?.date) ? formatPartialDate(d.date) : '',
                title: (d as IUPDRSIII).testType === 'UPDRS' ? fm('updrs.updrsIII') : fm('updrs.mds'),
                value: updrsIIIValue(d),
                key: 'updrsIII',
              };

            case 'ftmtrs':
              return {
                date: isPartialDate(d?.date) ? formatPartialDate(d.date) : '',
                title: fm('updrs.ftmtrsShort'),
                value: ftmtrsValue(d, id => formatMessage({ id })),
                key: 'ftmtrs',
              };

            case 'levodopatest':
              return {
                date: isPartialDate(d?.date) ? formatPartialDate(d.date) : '',
                title: fm('updrs.levodopatest'),
                value:
                  ((d as ILevodopatest).manualScore ||
                    Math.round(calculateLevodopaDBS(documents, d as ILevodopatest) || 0)) + '%',
                key: 'levodopatest',
              };

            case 'dbsEffect':
              return {
                date: isPartialDate(d?.date) ? formatPartialDate(d.date) : '',
                title: fm('updrs.dbs'),
                value:
                  ((d as IDBSEffect).manualScore || Math.round(calculateLevodopaDBS(documents, d as IDBSEffect) || 0)) +
                  '%',
                key: 'dbsEffect',
              };

            default:
              return {
                date: isPartialDate(d?.date) ? formatPartialDate(d.date) : '',
                title: fm('updrs.default'),
                key: 'default',
              };
          }
        })}
      />
      {updrsMissingTypes.length > 0 ? (
        <div style={{ color: colors.darkGray, fontSize: '1.4rem', marginTop: '0.2rem' }}>
          <FormattedMessage id={updrsMissingTypes.length === 4 ? 'general.theseMeters' : 'general.alsoTheseMeters'} />:{' '}
          {updrsMissingTypes.map(type => updrsTypesDisplayed(type!)).join(', ')}
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

interface IOwnProps {
  documents: TDocument[];
}

export default UpdrsDash;
